import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import { BoxTruckLoadAnnotation, BoxTruckOperatorBid, LoadBid } from '@numeo/types'
import { CanonicalLoad } from '@numeo/types-core'
import { useSnackbar } from '@/contexts/SnackbarContext'
import { SnackbarType } from '@/utils/snackbar'
import {
    BoxTruckEmailThread,
    fetchBoxTruckAnnotationsByExternalId,
    fetchBoxTruckBidsByExternalId,
    fetchBoxTruckEmailThreads,
    fetchBoxTruckOperatorBidsByExternalId,
    setBoxTruckAnnotation,
} from '../services/boxTruckLoadsApi'
import { loadKey } from '../utils/loadFields'
import { trackBoxTruckNoteSaved, trackBoxTruckRatingSet } from '../utils/analytics'

export type { BoxTruckEmailThread }

/**
 * Ids per overlay lookup. The server takes the FIRST 500 of an overlay body and drops the rest silently
 * (`MAX_OVERLAY_IDS` in main-service's box-truck router), so a board past that mark lost its bids, its team
 * ratings and its operator-contacted badges with nothing to say so — and the email-thread route answers 400
 * above 2 000. Asking in slices the caps cannot trim keeps the answer complete whatever the board holds.
 */
const OVERLAY_CHUNK = 500

/** What a sliced lookup came back with: the answers that arrived, plus the loads nobody answered for. */
interface ChunkedAnswer<T> {
    data: Record<string, T>
    /** Keys from slices that FAILED — unknown, not absent. The caller carries their previous values over. */
    unanswered: string[]
}

/**
 * Run an overlay lookup over `loads` in slices and merge the answers.
 *
 * A slice that FAILS contributes nothing rather than an empty map — the callers treat `null` as "keep what
 * is mapped" precisely so a blip cannot wipe a live bid, and one failed slice must not turn its loads into
 * confident absences. It reports WHICH loads went unanswered so even a rebuild can keep what it knew about
 * them. Only an all-failed run reports `null`, the shape that means "keep everything".
 */
const fetchInChunks = async <T>(
    loads: CanonicalLoad[],
    fetcher: (slice: CanonicalLoad[]) => Promise<Record<string, T> | null>
): Promise<ChunkedAnswer<T> | null> => {
    const slices: CanonicalLoad[][] = []
    for (let i = 0; i < loads.length; i += OVERLAY_CHUNK) slices.push(loads.slice(i, i + OVERLAY_CHUNK))
    if (slices.length <= 1) {
        const only = await fetcher(loads)
        return only === null ? null : { data: only, unanswered: [] }
    }
    const answers = await Promise.all(slices.map(fetcher))
    if (answers.every((answer) => answer === null)) return null
    const data: Record<string, T> = {}
    const unanswered: string[] = []
    answers.forEach((answer, index) => {
        if (answer === null) unanswered.push(...slices[index].map(loadKey))
        else Object.assign(data, answer)
    })
    return { data, unanswered }
}

interface BoxTruckOverlays {
    /** Driver/carrier bids on the visible loads, keyed by raw externalId (loadKey). */
    bids: Record<string, LoadBid[]>
    /** Successful bid emails sent by operators, keyed by load id. */
    operatorBids: Record<string, BoxTruckOperatorBid[]>
    /** The rating + note this viewer may see for each load, keyed by raw externalId. With Groups enabled
     *  that is their own desk's opinion, falling back to the company-wide one. */
    annotations: Record<string, BoxTruckLoadAnnotation>
    /** Broker email-thread state per load (thread id + unread replies), keyed by raw externalId. */
    emailThreads: Record<string, BoxTruckEmailThread>
    /**
     * The loads the thread overlay has really ANSWERED for, keyed as the board keys a load.
     *
     * These four overlays are four independent requests, and one question needs two of them: "has a driver
     * bid and nobody here has emailed about it" pins a row to the top. With the bids back and the threads
     * not, every bid load answered that question yes and jumped up, then half of them jumped back down a
     * moment later — so the caller asks this before pinning anything.
     *
     * A SET rather than a flag, because the answer is per load and partial answers are routine: a lookup is
     * sliced under the server's id cap and one slice can fail while the rest succeed, and an appended page
     * arrives with its bids before its threads. A flag would call a failed slice's loads "answered, no
     * thread" — which is the jump again — or un-pin the whole board on every load-more.
     */
    threadsKnownFor: ReadonlySet<string>
    /** Set the rating + note on a load (optimistic; teammates see it on their next refresh). `kind` picks
     *  the success toast copy ("Rating saved" / "Note saved"). */
    setAnnotation: (load: CanonicalLoad, rating: number | null, comment: string | null, kind: 'rating' | 'note') => Promise<void>
    /** Best-effort refetch of all three overlays for the given loads; skips setState once `signal` aborts. */
    /**
     * Refetch the overlays for `loads`. `mode: 'merge'` folds the result into what is already mapped — for a
     * page appended onto a board whose earlier pages were fetched with their own overlays; `'replace'` (the
     * default) is for a board that was just rebuilt, so a previous search's entries cannot linger.
     */
    fetchOverlaysFor: (loads: CanonicalLoad[], signal: AbortSignal, mode?: 'replace' | 'merge') => void
}

/**
 * The per-load overlays shown on the Box Truck board — driver bids, operator bid-emails, and the team's
 * shared rating/note — plus their live WebSocket upserts and the team-annotation writer. Split out of
 * {@link useBoxTruckLoads} so that hook stays focused on the search / pagination feed.
 */
export const useBoxTruckOverlays = (currentOperatorId?: string): BoxTruckOverlays => {
    // No `alerts` switch any more: it existed so a second consumer of this hook would not ding and toast a
    // teammate's rating twice. With the rating push gone there is nothing to ding — the only snackbars left
    // confirm this operator's OWN write, which is per-call and cannot double up.
    const { showSnackbar } = useSnackbar()
    const [bids, setBids] = useState<Record<string, LoadBid[]>>({})
    const [operatorBids, setOperatorBids] = useState<Record<string, BoxTruckOperatorBid[]>>({})
    const [annotations, setAnnotations] = useState<Record<string, BoxTruckLoadAnnotation>>({})
    const [emailThreads, setEmailThreads] = useState<Record<string, BoxTruckEmailThread>>({})
    const [threadsKnownFor, setThreadsKnownFor] = useState<ReadonlySet<string>>(new Set())

    // Overlays are independent and best-effort. A null result means the fetch FAILED (not "no data") — keep
    // the last-known overlay rather than wiping it, so a transient blip on a frequent auto-refresh tick
    // doesn't drop live bids/ratings.
    const fetchOverlaysFor = useCallback((loads: CanonicalLoad[], signal: AbortSignal, mode: 'replace' | 'merge' = 'replace') => {
        const apply = <T>(setState: Dispatch<SetStateAction<Record<string, T>>>, next: ChunkedAnswer<T> | null) => {
            if (!next || signal.aborted) return
            setState((prev) => {
                if (mode === 'merge') return { ...prev, ...next.data }
                // A REBUILD drops what is no longer on the board — but only for loads this run actually
                // heard about. A slice that failed knows nothing, so its loads keep the overlay they had;
                // replacing the map with the partial answer would delete live bids on a transient blip,
                // which is the very thing the null-means-keep contract exists to prevent.
                if (next.unanswered.length === 0) return next.data
                const carried: Record<string, T> = {}
                for (const key of next.unanswered) if (prev[key] !== undefined) carried[key] = prev[key]
                return { ...carried, ...next.data }
            })
        }
        void fetchInChunks(loads, fetchBoxTruckBidsByExternalId).then((next) => apply(setBids, next))
        void fetchInChunks(loads, fetchBoxTruckOperatorBidsByExternalId).then((next) => apply(setOperatorBids, next))
        void fetchInChunks(loads, fetchBoxTruckAnnotationsByExternalId).then((next) => apply(setAnnotations, next))
        const asked = loads.map(loadKey)
        void fetchInChunks(loads, fetchBoxTruckEmailThreads).then((next) => {
            apply(setEmailThreads, next)
            if (signal.aborted) return
            /**
             * WHICH LOADS WE REALLY HEARD ABOUT — the ones asked for, less the slices that failed. `null` is
             * the all-failed shape, so it adds nobody.
             *
             * A rebuild REPLACES the set, because the rows it did not ask about are not on the board any
             * more; an append unions, because the earlier pages' answers still stand.
             */
            const unanswered = new Set(next?.unanswered ?? asked)
            const answered = asked.filter((key) => !unanswered.has(key))
            setThreadsKnownFor((prev) => (mode === 'merge' ? new Set([...prev, ...answered]) : new Set(answered)))
        })
    }, [])

    // Live driver bids: main-service pushes a BID_PLACED per bid; upsert it into the map (replace the same
    // bidder's prior bid, else append), keeping each load's list sorted best-price-first.
    useEffect(() => {
        const onBidPlaced = (e: Event) => {
            const bid = (e as CustomEvent<{ bid: LoadBid }>).detail?.bid
            if (!bid?.externalId) return
            setBids((prev) => {
                const others = (prev[bid.externalId] ?? []).filter((b) => b.bidderId !== bid.bidderId)
                return { ...prev, [bid.externalId]: [...others, bid].sort((a, b) => b.price - a.price) }
            })
            // The operator-facing alert (ding + a persistent, expandable bottom-right card) is owned by the
            // app-wide BoxTruckBidNotifications component; here we only fold the bid into the Bids column.
        }
        window.addEventListener('box-truck:bid-placed', onBidPlaced)
        return () => window.removeEventListener('box-truck:bid-placed', onBidPlaced)
    }, [])

    // A successful email bid is persisted as the operator's existing interaction record, then pushed through
    // the same Box Truck WebSocket channel as driver bids. Silently upsert it by operator so teammates keep
    // the duplicate-outreach guard current without receiving a disruptive sound or snackbar. Re-sends refresh
    // the existing operator row instead of creating duplicates.
    useEffect(() => {
        const onOperatorBidEmailSent = (e: Event) => {
            const bid = (e as CustomEvent<BoxTruckOperatorBid>).detail
            if (!bid?.externalId || !bid.operatorId) return
            setOperatorBids((prev) => {
                const others = (prev[bid.externalId] ?? []).filter((item) => item.operatorId !== bid.operatorId)
                return { ...prev, [bid.externalId]: [{ ...bid }, ...others] }
            })
            // A sent bid email means the load now HAS a thread — flip the Contact icon live; the next overlay
            // tick fills in the real thread id (the WS event doesn't carry it).
            setEmailThreads((prev) => (prev[bid.externalId] ? prev : { ...prev, [bid.externalId]: { emailThreadId: '', unreadCount: 0 } }))
        }
        window.addEventListener('box-truck:operator-bid-email-sent', onOperatorBidEmailSent)
        return () => window.removeEventListener('box-truck:operator-bid-email-sent', onOperatorBidEmailSent)
    }, [])

    // Set the rating + note on a load: optimistically update, then persist. There is no realtime echo —
    // a rating is not time-critical, so teammates pick it up on the next overlay refresh (every few seconds)
    // instead of over a socket. A success toast confirms our own write.
    const setAnnotation = useCallback(
        async (load: CanonicalLoad, rating: number | null, comment: string | null, kind: 'rating' | 'note') => {
            const key = loadKey(load)
            // Snapshot the prior value so a failed save can be rolled back — otherwise the unsaved rating/note
            // would linger on the board (with just an error toast) until a refresh or a teammate's WS echo.
            let prior: BoxTruckLoadAnnotation | undefined
            setAnnotations((prev) => {
                prior = prev[key]
                return {
                    ...prev,
                    [key]: { externalId: key, rating, comment, updatedBy: currentOperatorId ?? '', updatedByEmail: null, updatedAt: new Date().toISOString() },
                }
            })
            try {
                const saved = await setBoxTruckAnnotation(load, rating, comment)
                setAnnotations((prev) => ({ ...prev, [saved.externalId]: saved }))
                showSnackbar(SnackbarType.SUCCESS, kind === 'rating' ? 'Rating saved' : 'Note saved')
                if (kind === 'rating') trackBoxTruckRatingSet(rating ?? 0)
                else trackBoxTruckNoteSaved()
            } catch {
                // Roll back the optimistic update (restore the prior value, or drop the key if there was none).
                setAnnotations((prev) => {
                    const next = { ...prev }
                    if (prior) next[key] = prior
                    else delete next[key]
                    return next
                })
                showSnackbar(SnackbarType.ERROR, 'Failed to save the rating / note')
            }
        },
        [currentOperatorId, showSnackbar]
    )

    return { bids, operatorBids, annotations, emailThreads, threadsKnownFor, setAnnotation, fetchOverlaysFor }
}
